import React, { useEffect, useRef, useState } from 'react';
import AGTableWrapper from 'components/AGTable/AGTableWrapper';
import AGMessage from 'components/AGMessage/AGMessage';
import Loader from 'components/Loader/Loader';
import { ModalRef } from 'components/Modal/Modal';
import CaretIcon from 'components/Icons/CaretIcon';
import { tenantsParkingClient } from 'clients';
import { SpotInterface } from 'types';
import TenantModal from './TenantModal';
import { TenantSummary, tenantsFromBulkRentals } from './utils';
import styles from './Tenants.module.css';

type TenantsProps = {
    spot: SpotInterface;
    month: string;
};

const Tenants = ({ spot, month }: TenantsProps) => {
    const [tenants, setTenants] = useState<TenantSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null);
    const modalRef = useRef<ModalRef>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErrorMessage('');
        (async () => {
            try {
                const bulkRentals = await tenantsParkingClient.getTenantsParking({ spotUuid: spot.uuid });
                if (!cancelled) setTenants(tenantsFromBulkRentals(bulkRentals));
            } catch (e) {
                if (!cancelled) setErrorMessage(e instanceof Error ? e.message : 'Failed to load tenants.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [spot.uuid]);

    function openTenant(tenant: TenantSummary) {
        setSelectedTenant(tenant);
        modalRef.current?.openModal();
    }

    if (loading) return <Loader />;
    if (errorMessage) return <AGMessage color="error" title={errorMessage} />;

    return (
        <div className={styles.page}>
            <AGTableWrapper>
                <thead>
                    <tr>
                        <th>Tenant</th>
                        <th aria-label="Open tenant" />
                    </tr>
                </thead>
                <tbody>
                    {tenants.map((tenant) => (
                        <tr key={tenant.uuid} className={styles.clickableRow} onClick={() => openTenant(tenant)}>
                            <td>{tenant.name}</td>
                            <td className={styles.caretCell}>
                                <CaretIcon direction="right" className={styles.caret} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </AGTableWrapper>
            {tenants.length === 0 && <p className={styles.empty}>No tenants at this property.</p>}
            <TenantModal
                ref={modalRef}
                tenant={selectedTenant}
                month={month}
                timeZone={spot.timezone}
                launchDate={spot.launch_date}
                onClose={() => setSelectedTenant(null)}
            />
        </div>
    );
};

export default Tenants;
